/**
 * client/src/lib/webauthn-prf-extension.ts
 *
 * Decode `extensions.prf.eval.first/second` from Base64URL → Uint8Array before
 * handing `optionsJSON` to @simplewebauthn/browser's startRegistration /
 * startAuthentication. v13 of that library does NOT decode PRF extension inputs
 * — it only decodes challenge + user.id + allowCredentials[].id — so the raw
 * Base64URL string otherwise reaches navigator.credentials.create/get, where
 * Chrome rejects it with:
 *
 *   "Failed to read the 'first' property from 'AuthenticationExtensionsPRFValues':
 *    The provided value is not of type '(ArrayBuffer or ArrayBufferView)'."
 */

// Re-export extractPrfOutput so callers can get all PRF helpers from one module.
export { extractPrfOutput } from "@/lib/prf-key-derivation";

function base64UrlToBytes(b64url: string): Uint8Array {
  const normalized = b64url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

/**
 * Extract the PRF eval salt from a credential options object.
 *
 * The PRF extension eval salt is stored as a Base64URL-encoded byte string in
 * `options.extensions.prf.eval.first`.  This helper decodes it to a lowercase
 * hex string suitable for use as an HKDF salt parameter.
 *
 * Returns null if the options do not contain a PRF eval salt.
 */
export function extractPrfSaltFromOptions(options: Record<string, unknown>): string | null {
  try {
    const ext = options.extensions as Record<string, unknown> | undefined;
    if (!ext) return null;
    const prf = ext.prf as { eval?: { first?: string } } | undefined;
    const b64 = prf?.eval?.first;
    if (!b64) return null;
    const normalized = b64.replace(/-/g, "+").replace(/_/g, "/");
    const binary = atob(normalized);
    const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
    return Array.from(bytes).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

export function decodePrfExtensionInputs<T extends { extensions?: unknown }>(options: T): T {
  const ext = options.extensions as
    | { prf?: { eval?: { first?: unknown; second?: unknown } } }
    | undefined;
  const prf = ext?.prf?.eval;
  if (!prf) return options;

  const next = { ...prf } as { first?: unknown; second?: unknown };
  if (typeof prf.first === "string") next.first = base64UrlToBytes(prf.first);
  if (typeof prf.second === "string") next.second = base64UrlToBytes(prf.second);

  return {
    ...options,
    extensions: { ...(options.extensions as object), prf: { eval: next } },
  };
}
